import React, { useState, useEffect } from 'react';
import { Building2, Tag, FileText, CheckCircle2, X, UploadCloud, Send, Loader2, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import LeadProfile from './components/LeadProfile';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sortStatuses } from '../../utils/statusUtils';
import Dropdown from '../../components/Dropdown';
import { API_URL, BASE_URL } from '../../utils/api';
import { getStateOptions, getCityOptions, getCitiesForState } from '../../utils/locationData';

const normalizeMachineType = (raw) => {
  if (!raw) return '';
  const s = raw.toString().trim().toLowerCase();
  if (s === 'rc' || s.includes('rc')) return 'rc';
  if (s === 'rental' || s.includes('rent')) return 'rental';
  if (s === 'new' || s.includes('new')) return 'new';
  return raw;
};

const isMachineInterest = (val) => {
  if (!val) return false;
  const s = val.toString().trim().toLowerCase();
  return s.includes('machine') || s.includes('future') || s === 'interested in machine' || s === 'in future';
};

const SOCIAL_MEDIA_OPTIONS = [
  { value: 'Instagram', label: 'Instagram' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'YouTube', label: 'YouTube' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Twitter (X)', label: 'Twitter (X)' },
  { value: 'Other', label: 'Other' }
];

const parseTimeToDate = (timeStr, baseDate = null) => {
  if (!timeStr) return null;
  const d = baseDate ? new Date(baseDate) : new Date();
  const str = timeStr.toString().trim();
  const [timePart, modifier] = str.split(/\s+/);
  const parts = timePart.split(':');
  let hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1] || '0', 10);
  if (isNaN(hours)) return null;
  if (modifier) {
    if (modifier.toLowerCase() === 'pm' && hours < 12) hours += 12;
    if (modifier.toLowerCase() === 'am' && hours === 12) hours = 0;
  }
  d.setHours(hours, isNaN(minutes) ? 0 : minutes, 0, 0);
  return d;
};

const AddLead = ({ onClose, initialData, isViewMode = false, onEdit, onDelete, onAction }) => {
  const { user: currentUser } = useAuth();

  const [shopName, setShopName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sameAsShop, setSameAsShop] = useState(false);
  const [address, setAddress] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('Surat');
  const [state, setState] = useState('Gujarat');
  const [isOtherState, setIsOtherState] = useState(false);
  const [otherState, setOtherState] = useState('');
  const [isOtherCity, setIsOtherCity] = useState(false);
  const [otherCity, setOtherCity] = useState('');
  const [pincode, setPincode] = useState('');

  const [agencyType, setAgencyType] = useState('copy shop');
  const [isOtherAgencyType, setIsOtherAgencyType] = useState(false);
  const [otherAgencyType, setOtherAgencyType] = useState('');

  const [interestedType, setInterestedType] = useState('');
  const [machineType, setMachineType] = useState('');
  const [machineBrand, setMachineBrand] = useState('');
  const [otherInterest, setOtherInterest] = useState('');
  const [machineModel, setMachineModel] = useState('');

  const [assignee, setAssignee] = useState(currentUser?.name || '');
  const [status, setStatus] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [leadSource, setLeadSource] = useState('IndiaMART');
  const [referenceName, setReferenceName] = useState('');
  const [socialMediaPlatform, setSocialMediaPlatform] = useState('');
  const [notes, setNotes] = useState('');

  const isReferenceSource = (leadSource || '').toLowerCase().includes('reference');
  const isSocialMediaSource = (leadSource || '').toLowerCase().includes('social');

  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpDays, setFollowUpDays] = useState('');
  const [originalFollowUp, setOriginalFollowUp] = useState(null);
  const [followUpId, setFollowUpId] = useState(null);
  const [executiveSchedule, setExecutiveSchedule] = useState({ followups: [], deliveries: [] });
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);

  const [frontCard, setFrontCard] = useState(null);
  const [backCard, setBackCard] = useState(null);

  const [showBrochureModal, setShowBrochureModal] = useState(false);
  const [createdLead, setCreatedLead] = useState(null);

  // Dynamic master data state
  const [leadTypes, setLeadTypes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const userRole = (currentUser?.role || '').toLowerCase();
  const canAssignExecutive = userRole.includes('admin') || userRole.includes('telecaller') || userRole.includes('tele-caller');

  useEffect(() => {
    if (!initialData && currentUser?.name && (!assignee || !canAssignExecutive)) {
      setAssignee(currentUser.name);
    }
  }, [currentUser, initialData, canAssignExecutive, assignee]);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!assignee || !followUpDate) {
        setExecutiveSchedule({ followups: [], deliveries: [] });
        return;
      }
      const userObj = users.find(u => u.name === assignee || u.name + ' (' + u.role + ')' === assignee);
      const assigneeId = userObj ? userObj.id : (currentUser?.name === assignee ? currentUser?.id : null);

      if (!assigneeId) return;

      setIsFetchingSchedule(true);
      try {
        const { default: api } = await import('../../utils/api');
        const d = new Date(followUpDate);
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const res = await api.get(`/users/${assigneeId}/schedule?date=${dateString}&_t=${Date.now()}`);
        setExecutiveSchedule(res.data);
      } catch (err) {
        console.error('Failed to fetch executive schedule', err);
        setExecutiveSchedule({ followups: [], deliveries: [] });
      } finally {
        setIsFetchingSchedule(false);
      }
    };
    fetchSchedule();
  }, [assignee, followUpDate, users, currentUser]);

  useEffect(() => {


    const fetchMasters = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const [typesRes, machinesRes, sourcesRes, statusesRes, usersRes, customerTypesRes] = await Promise.all([
          api.get('/masters/lead-types').catch(() => ({ data: [] })),
          api.get('/masters/machines').catch(() => ({ data: [] })),
          api.get('/masters/lead-sources').catch(() => ({ data: [] })),
          api.get('/masters/status').catch(() => ({ data: [] })),
          api.get('/auth/users').catch(() => ({ data: [] })),
          api.get('/masters/customer-types').catch(() => ({ data: [] }))
        ]);
        setLeadTypes(typesRes.data.filter(t => t.status === 'Active' && t.name && t.name.trim() !== '' && t.name.toLowerCase() !== 'copy shop'));
        setMachines(machinesRes.data.filter(m => m.status === 'Active' && m.name && m.name.trim() !== ''));

        const activeSources = sourcesRes.data.filter(s => s.status === 'Active');
        setLeadSources(activeSources);
        if (!initialData) {
          const defaultSource = activeSources.find(s => {
            const n = (s.name || '').toLowerCase().replace(/[\s-_]/g, '');
            return n.includes('indiamart');
          }) || activeSources.find(s => (s.name || '').toLowerCase().includes('india')) || activeSources[0];
          if (defaultSource) setLeadSource(defaultSource.name);
        }

        const activeStatuses = sortStatuses(statusesRes.data.filter(s => s.status === 'Active'));
        setStatuses(activeStatuses);
        setUsers(usersRes.data.filter(u => u.role !== 'Admin' && u.status === 'Active'));
        setCustomerTypes(customerTypesRes.data.filter(c => c.status === 'Active'));

        if (!initialData && activeStatuses.length > 0) {
          const newStatus = activeStatuses.find(s => s.name.toLowerCase().includes('new')) || activeStatuses[0];
          setStatus(newStatus.name);
        }
        if (!initialData) {
          const activeCustomerTypes = customerTypesRes.data.filter(c => c.status === 'Active');
          const hotType = activeCustomerTypes.find(c => c.name.toLowerCase() === 'hot') || activeCustomerTypes[0];
          if (hotType) setCustomerType(hotType.name);
        }
      } catch (err) {
        console.error('Failed to fetch master data', err);
      }
    };
    fetchMasters();
  }, []);

  useEffect(() => {
    if (leadSources.length > 0 && !initialData) {
      const match = leadSources.find(s => s.name.toLowerCase() === (leadSource || '').toLowerCase()) ||
        leadSources.find(s => {
          const n = (s.name || '').toLowerCase().replace(/[\s-_]/g, '');
          return n.includes('indiamart');
        }) || leadSources.find(s => (s.name || '').toLowerCase().includes('india')) || leadSources[0];
      if (match && leadSource !== match.name) {
        setLeadSource(match.name);
      }
    }
  }, [leadSources, initialData, leadSource]);



  useEffect(() => {
    if (leadTypes.length > 0 && initialData) {
      const rawInterest =
        initialData.interestedType ||
        initialData.interestType ||
        initialData.type ||
        (typeof initialData.leadType === 'object' ? initialData.leadType?.name : initialData.leadType) ||
        '';
      const currentOrRaw = (interestedType || rawInterest).toString().trim().toLowerCase();
      const match = leadTypes.find(lt =>
        lt.name.toLowerCase() === currentOrRaw ||
        (currentOrRaw.includes('machine') && lt.name.toLowerCase().includes('machine')) ||
        (currentOrRaw.includes('future') && lt.name.toLowerCase().includes('future')) ||
        (currentOrRaw.includes('not') && lt.name.toLowerCase().includes('not'))
      );
      if (match && interestedType !== match.name) {
        setInterestedType(match.name);
      }
    }
  }, [leadTypes, initialData]);

  useEffect(() => {
    if (initialData) {
      setShopName(initialData.companyName || initialData.name || '');
      setContactPerson(initialData.contactPerson || initialData.contact || '');
      setContactPhone(initialData.contactPhone || '');
      setContactEmail(initialData.contactEmail || '');
      setPhone(initialData.phone || '');
      setEmail(initialData.email || '');
      setFrontCard(initialData.frontCard || null);
      setBackCard(initialData.backCard || null);
      let rawAddr1 = initialData.address || '';
      let rawAddr2 = initialData.address2 || '';
      const cleanAddr1 = rawAddr1.replace(/\s*\[GPS:.*?\]/gi, '').trim();
      const cleanAddr2 = rawAddr2.replace(/\s*\[GPS:.*?\]/gi, '').trim();
      setAddress(cleanAddr1);
      setAddress2(cleanAddr2);
      const initCity = initialData.city || 'Surat';
      const initState = initialData.state || 'Gujarat';
      setCity(initCity);
      setState(initState);
      setIsOtherState(initState === 'Other');
      setIsOtherCity(initCity === 'Other');
      setPincode(initialData.pincode || '');
      setSameAsShop(false);

      let latVal = initialData.latitude;
      let lngVal = initialData.longitude;
      if ((latVal === undefined || latVal === null || latVal === '') && rawAddr1) {
        const gpsMatch = rawAddr1.match(/\[GPS:\s*Lat:\s*([\d.-]+),\s*Lon:\s*([\d.-]+)\]/i);
        if (gpsMatch) {
          latVal = parseFloat(gpsMatch[1]);
          lngVal = parseFloat(gpsMatch[2]);
        }
      }
      if (latVal !== undefined && latVal !== null && latVal !== '') setLatitude(latVal);
      if (lngVal !== undefined && lngVal !== null && lngVal !== '') setLongitude(lngVal);

      setAgencyType(initialData.agencyType || 'copy shop');
      setIsOtherAgencyType(initialData.otherAgencyType ? true : false);
      setOtherAgencyType(initialData.otherAgencyType || '');

      // Product Interest Population
      const rawInterest =
        initialData.interestedType ||
        initialData.interestType ||
        initialData.type ||
        (typeof initialData.leadType === 'object' ? initialData.leadType?.name : initialData.leadType) ||
        '';
      let matchedInterest = '';
      const lowerInterest = rawInterest.toString().trim().toLowerCase();
      if (lowerInterest.includes('machine') || lowerInterest === 'interested in machine') {
        matchedInterest = 'Interested in machine';
      } else if (lowerInterest.includes('future') || lowerInterest === 'in future') {
        matchedInterest = 'In Future';
      } else if (lowerInterest.includes('not') || lowerInterest === 'not at all') {
        matchedInterest = 'Not at all';
      } else if (rawInterest && rawInterest !== 'Unknown' && lowerInterest !== 'copy shop') {
        matchedInterest = rawInterest;
      } else if (
        initialData.machineType ||
        initialData.machineBrand ||
        initialData.machineModel ||
        (initialData.machine && initialData.machine !== 'Unknown')
      ) {
        matchedInterest = 'Interested in machine';
      }
      setInterestedType(matchedInterest);

      const rawMt = initialData.machineType || '';
      setMachineType(normalizeMachineType(rawMt));

      let rawBrand = initialData.machineBrand || initialData.machine?.name || '';
      if (!rawBrand && typeof initialData.machine === 'string' && initialData.machine !== 'Unknown') {
        rawBrand = initialData.machine.split(' - ')[0].trim();
      }
      setMachineBrand(rawBrand);

      let rawModel = initialData.machineModel || '';
      if (!rawModel && typeof initialData.machine === 'string' && initialData.machine !== 'Unknown' && initialData.machine.includes(' - ')) {
        rawModel = initialData.machine.split(' - ').slice(1).join(' - ').trim();
      }
      setMachineModel(rawModel);
      setOtherInterest(initialData.otherInterest || '');

      setStatus(initialData.status && initialData.status !== 'Unknown' ? (initialData.status.name || initialData.status) : '');
      setCustomerType(initialData.customerType || initialData.customerType?.name || '');
      setAssignee(initialData.assignee !== 'Unassigned' && initialData.assignee ? initialData.assignee : (currentUser?.name || ''));
      setLeadSource(initialData.source && initialData.source !== 'Unknown' ? (initialData.source?.name || initialData.source) : (initialData.leadSource?.name || initialData.leadSource || 'IndiaMART'));
      setReferenceName(initialData.referenceName || '');
      setSocialMediaPlatform(initialData.socialMediaPlatform || '');

      const rawStatusStr = initialData.status && initialData.status !== 'Unknown' ? (initialData.status.name || initialData.status) : '';
      const statusLower = (rawStatusStr || '').toLowerCase();
      const isInitialFollowOrVisit = rawStatusStr && (
        statusLower.includes('schedule') ||
        statusLower.includes('follow') ||
        statusLower.includes('visit')
      );

      let foundValidFollowup = false;
      if (initialData.followups && initialData.followups.length > 0) {
        const isInactiveFollowup = (f) => {
          if (!f || !f.date) return true;
          if (f.type === 'System') return true;
          const t = (f.type || '').toLowerCase();
          const n = (f.notes || '').toLowerCase();
          const rawNotes = f.notes || '';
          if (t.includes('cancel') || n.includes('cancel') || rawNotes.startsWith('[Cancel')) return true;
          if (t.includes('complete') || rawNotes.startsWith('[Completed]') || rawNotes.startsWith('[Complete')) return true;
          if (t.includes('closed') || rawNotes.startsWith('[Closed]')) return true;
          if (t.includes('reschedule') || rawNotes.startsWith('[Rescheduled]') || rawNotes.startsWith('[Reschedule')) return true;
          return false;
        };

        const activeFollowups = initialData.followups.filter(f => !isInactiveFollowup(f));

        let targetFollowup = null;

        // 1. If lead has nextFollowUp date, find the active followup matching that date
        if (initialData.nextFollowUp && activeFollowups.length > 0) {
          const nextTime = new Date(initialData.nextFollowUp).getTime();
          targetFollowup = activeFollowups.find(f => new Date(f.date).getTime() === nextTime);
        }

        // 2. Match active followup by status type (e.g. visit vs follow-up)
        if (!targetFollowup && activeFollowups.length > 0) {
          if (statusLower.includes('visit')) {
            targetFollowup = activeFollowups.find(f => (f.type || '').toLowerCase().includes('visit') || (f.notes || '').toLowerCase().includes('visit'));
          } else if (statusLower.includes('follow')) {
            targetFollowup = activeFollowups.find(f => (f.type || '').toLowerCase().includes('follow') || (f.notes || '').toLowerCase().includes('follow'));
          }
        }

        // 3. Fallback among active followups: future followups first, then most recent active
        if (!targetFollowup && activeFollowups.length > 0) {
          const now = new Date();
          const futureFollowups = activeFollowups.filter(f => new Date(f.date) >= now);
          futureFollowups.sort((a, b) => new Date(a.date) - new Date(b.date));
          targetFollowup = futureFollowups.length > 0 ? futureFollowups[0] : [...activeFollowups].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        }

        // 4. Fallback if no active followups exist: check all non-system followups
        if (!targetFollowup) {
          const nonSystem = initialData.followups.filter(f => f.type !== 'System' && f.date);
          if (nonSystem.length > 0) {
            targetFollowup = [...nonSystem].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
          }
        }

        if (targetFollowup && targetFollowup.date) {
          foundValidFollowup = true;
          const d = new Date(targetFollowup.date);
          const hours = String(d.getHours()).padStart(2, '0');
          const mins = String(d.getMinutes()).padStart(2, '0');
          setFollowUpTime(`${hours}:${mins}`);
          setOriginalFollowUp(d.getTime());
          setFollowUpId(targetFollowup.id);

          const dDate = new Date(d);
          dDate.setHours(0, 0, 0, 0);
          const nDate = new Date();
          nDate.setHours(0, 0, 0, 0);
          const diffDays = Math.round((dDate - nDate) / (1000 * 60 * 60 * 24));
          if (diffDays >= 0) {
            setFollowUpDays(diffDays);
            setFollowUpDate(d);
          } else {
            setFollowUpDays(0);
            setFollowUpDate(new Date());
          }
        }
      }

      if (!foundValidFollowup && isInitialFollowOrVisit) {
        setFollowUpDays(0);
        setFollowUpDate(new Date());
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const mins = String(now.getMinutes()).padStart(2, '0');
        setFollowUpTime(`${hours}:${mins}`);
      }
    }
  }, [initialData]);

  // Handle calculation for follow up days
  const handleFollowUpDaysChange = (days) => {
    setFollowUpDays(days);
    if (days !== '' && days !== null && days !== undefined && !isNaN(days)) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(days, 10));
      setFollowUpDate(d);
      if (errors.followUpDays) setErrors(prev => ({ ...prev, followUpDays: null }));
      if (errors.followUpDate) setErrors(prev => ({ ...prev, followUpDate: null }));
    } else {
      setFollowUpDate(null);
    }
  };

  const handleSameAsShopChange = (checked) => {
    setSameAsShop(checked);
    if (checked) {
      setContactPerson(shopName || '');
      setContactPhone(phone || '');
      setContactEmail(email || '');
      setErrors(prev => ({
        ...prev,
        contactPerson: null,
        contactPhone: null,
        contactEmail: null
      }));
    }
  };

  useEffect(() => {
    if (sameAsShop) {
      setContactPerson(shopName || '');
      setContactPhone(phone || '');
      setContactEmail(email || '');
    }
  }, [sameAsShop, shopName, phone, email]);

  const handleFollowUpDateChange = (date) => {
    setFollowUpDate(date);
    if (date) {
      const dDate = new Date(date);
      dDate.setHours(0, 0, 0, 0);
      const nDate = new Date();
      nDate.setHours(0, 0, 0, 0);
      const diffDays = Math.round((dDate - nDate) / (1000 * 60 * 60 * 24));
      setFollowUpDays(diffDays >= 0 ? diffDays : 0);
      if (errors.followUpDate) setErrors(prev => ({ ...prev, followUpDate: null }));
      if (errors.followUpDays) setErrors(prev => ({ ...prev, followUpDays: null }));
    } else {
      setFollowUpDays('');
    }
  };

  const handleInterestTypeChange = (val) => {
    setInterestedType(val);
    if (errors.interestedType) setErrors(prev => ({ ...prev, interestedType: null }));
    if (val === 'In Future') {
      const scheduleStatus = statuses.find(s => s.name.toLowerCase().includes('follow') || s.name.toLowerCase().includes('visit') || s.name.toLowerCase().includes('schedule'));
      if (scheduleStatus) {
        setStatus(scheduleStatus.name);
        if (followUpDays === '' || followUpDays === null || followUpDays === undefined) {
          handleFollowUpDaysChange(7);
        }
        if (!followUpTime) {
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, '0');
          const mins = String(now.getMinutes()).padStart(2, '0');
          setFollowUpTime(`${hours}:${mins}`);
        }
      }
    } else if (val === 'Not at all' || val === 'Not at all of use') {
      const lostStatus = statuses.find(s => s.name.toLowerCase().includes('lost'));
      if (lostStatus) setStatus(lostStatus.name);
    }
  };

  const getInterestTypeOptions = () => {
    const standardTypes = ['Interested in machine', 'In Future', 'Not at all'];
    let opts = [];
    if (leadTypes && leadTypes.length > 0) {
      opts = leadTypes
        .filter(t => t.name && t.name.trim() !== '' && t.name.toLowerCase() !== 'copy shop')
        .map(t => ({ value: t.name, label: t.name }));
    } else {
      opts = standardTypes.map(t => ({ value: t, label: t }));
    }
    standardTypes.forEach(defVal => {
      if (!opts.some(o => o.value.toLowerCase() === defVal.toLowerCase())) {
        opts.push({ value: defVal, label: defVal });
      }
    });
    if (interestedType && interestedType.toLowerCase() !== 'copy shop' && !opts.some(o => o.value.toLowerCase() === interestedType.toLowerCase())) {
      opts.unshift({ value: interestedType, label: interestedType });
    }
    return opts;
  };



  const handleSave = async (e) => {
    e.preventDefault();
    if (isViewMode) return;
    // Phone Validations
    const newErrors = {};
    const phoneRegex = /^\d{10}$/;
    if (!shopName?.trim()) newErrors.shopName = 'Agency / Shop Name is required';
    if (!phone?.trim()) newErrors.phone = 'Phone Number is required';
    else if (!phoneRegex.test(phone.replace(/\D/g, ''))) newErrors.phone = 'Phone number must be exactly 10 digits';

    if (email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    const finalState = (state === 'Other' || isOtherState) ? otherState?.trim() : state?.trim();
    const finalCity = (city === 'Other' || isOtherCity) ? otherCity?.trim() : city?.trim();

    if (!finalState) newErrors.state = 'State is required';
    if (!finalCity) newErrors.city = 'City is required';

    if (contactPhone?.trim() && !phoneRegex.test(contactPhone.replace(/\D/g, ''))) {
      newErrors.contactPhone = 'Phone number must be exactly 10 digits';
    }

    if (contactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactEmail)) newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (!leadSource) newErrors.leadSource = 'Please select a Lead Source';
    if (isReferenceSource && !referenceName?.trim()) newErrors.referenceName = 'Please enter reference name';
    if (isSocialMediaSource && !socialMediaPlatform?.trim()) newErrors.socialMediaPlatform = 'Please select a social media platform';
    if (!agencyType) newErrors.agencyType = 'Please select an Agency / Shop Type';
    if (agencyType === 'other' && !otherAgencyType) newErrors.otherAgencyType = 'Please specify Other Agency Type';

    if (!interestedType) newErrors.interestedType = 'Please select an Interest Type';
    if (isMachineInterest(interestedType)) {
      if (!machineType) newErrors.machineType = 'Please select a Machine Type';
      if (!machineBrand) newErrors.machineBrand = 'Please select a Machine Brand';
      if (!machineModel) newErrors.machineModel = 'Please specify a Machine Model';
    }

    if (!status) newErrors.status = 'Please select an Initial Status';
    const effectiveAssignee = canAssignExecutive ? assignee : (assignee || currentUser?.name || '');
    if (!effectiveAssignee) newErrors.assignee = 'Please assign an Executive';
    if (!customerType) newErrors.customerType = 'Please select a Customer Type';

    const isFollowUpOrVisitStatus = status && (
      status.toLowerCase().includes('schedule') ||
      status.toLowerCase().includes('follow') ||
      status.toLowerCase().includes('visit')
    );
    if (isFollowUpOrVisitStatus) {
      if (followUpDays === '' || followUpDays === null || followUpDays === undefined || isNaN(Number(followUpDays))) {
        newErrors.followUpDays = 'Please specify follow-up days';
      }
      if (!followUpDate) newErrors.followUpDate = 'Please select a follow-up date';
      if (!followUpTime) newErrors.followUpTime = 'Please select a follow-up time';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      const { default: api } = await import('../../utils/api');

      const machineObj = machines.find(m => m.name === machineBrand);
      const leadTypeObj = leadTypes.find(lt => lt.name.toLowerCase() === (interestedType || '').toLowerCase());
      const sourceObj = leadSources.find(s => s.name === leadSource);
      const statusObj = statuses.find(s => s.name === status);
      const customerTypeObj = customerTypes.find(c => c.name === customerType);
      const userObj = users.find(u => u.name === effectiveAssignee || u.name + ' (' + u.role + ')' === effectiveAssignee) ||
        (currentUser?.name === effectiveAssignee ? currentUser : null);

      let finalFollowUpDate = null;
      if (followUpDate) {
        const d = parseTimeToDate(followUpTime, followUpDate) || new Date(followUpDate);
        finalFollowUpDate = d.toISOString();
      }

      let frontCardBase64 = frontCard;
      let backCardBase64 = backCard;

      const toBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });

      if (frontCard instanceof File) {
        frontCardBase64 = await toBase64(frontCard);
      }
      if (backCard instanceof File) {
        backCardBase64 = await toBase64(backCard);
      }

      const payload = {
        companyName: shopName,
        contactPerson: contactPerson,
        contactPhone: contactPhone,
        contactEmail: contactEmail,
        phone: phone,
        email: email,
        address: address,
        address2: address2,
        city: (city === 'Other' || isOtherCity) ? otherCity.trim() : city,
        state: (state === 'Other' || isOtherState) ? otherState.trim() : state,
        pincode: pincode,
        agencyType: agencyType,
        otherAgencyType: isOtherAgencyType ? otherAgencyType : null,
        interestType: interestedType,
        leadTypeId: leadTypeObj ? leadTypeObj.id : (initialData?.leadTypeId || null),
        machineType: machineType,
        frontCard: frontCardBase64,
        backCard: backCardBase64,
        notes: notes,
        machineId: machineObj ? machineObj.id : (initialData?.machineId || initialData?.machine?.id || null),
        machineModel: machineModel,
        leadSourceId: sourceObj ? sourceObj.id : null,
        referenceName: isReferenceSource ? referenceName.trim() : null,
        socialMediaPlatform: isSocialMediaSource ? socialMediaPlatform : null,
        statusId: statusObj ? statusObj.id : null,
        customerTypeId: customerTypeObj ? customerTypeObj.id : null,
        latitude,
        longitude,
        isCustomer: statusObj ? ['won', 'completed', 'complete'].includes(statusObj.name.toLowerCase()) : false,
        userId: userObj ? userObj.id : (currentUser?.id || null),
        followUpDate: finalFollowUpDate,
        followUpTime: followUpTime || null,
        followUpId: followUpId
      };

      if (initialData && initialData.id) {
        await api.put(`/leads/${initialData.id}`, payload);
        toast.success('Lead updated successfully!');
        onClose(true);
      } else {
        const res = await api.post('/leads', payload);
        toast.success('Lead created successfully!');


        if (machineObj && machineObj.brochure) {
          setCreatedLead(res.data);
          setShowBrochureModal(true);
        } else {
          onClose(true);
        }
      }

    } catch (err) {
      console.error(err);
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        const firstError = Object.values(err.response.data.errors)[0];
        toast.error(typeof firstError === 'string' ? firstError : 'Please fix the errors in the form');
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to save lead');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendBrochureViaEmail = async () => {
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/leads/${createdLead.id}/send-brochure`, { baseUrl: BASE_URL });
      toast.success('Brochure sent via Email successfully!');
      setShowBrochureModal(false);
      onClose(true);
    } catch (err) {
      toast.error('Failed to send brochure via Email');
    }
  };

  const sendBrochureViaWhatsApp = () => {
    const phone = createdLead?.contactPhone || createdLead?.phone || '';
    const cleanPhone = phone.replace(/\D/g, '');
    const finalPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;

    const machineObj = machines.find(m => m.name === machineBrand);
    let brochureLink = '';
    if (machineObj && machineObj.brochure) {
      const path = machineObj.brochure.startsWith('/') ? machineObj.brochure : `/${machineObj.brochure}`;
      brochureLink = `\n\nBrochure: ${BASE_URL}${path}`;
    }

    const name = createdLead?.contactPerson || createdLead?.companyName || 'Customer';
    const message = encodeURIComponent(`Hello ${name},\n\nThank you for visiting us! Here are the details for the ${machineBrand} ${machineModel} machine you inquired about.${brochureLink}\n\nPlease let us know if you have any questions.`);

    window.open(`https://wa.me/${finalPhone}?text=${message}`, '_blank');

    toast.success('Opened WhatsApp!');
    setShowBrochureModal(false);
    onClose(true);
  };

  useEffect(() => {
    if (showBrochureModal) {
      const modalContainer = document.querySelector('.lead-modal-container');
      if (modalContainer) {
        modalContainer.classList.add('lead-brochure-modal');
      }
      return () => {
        if (modalContainer) {
          modalContainer.classList.remove('lead-brochure-modal');
        }
      };
    }
  }, [showBrochureModal]);

  if (isViewMode && initialData) {
    return <LeadProfile
      lead={initialData}
      onClose={onClose}
      onEditLead={onEdit}
      onDeleteLead={onDelete}
      onActionLead={onAction}
    />;
  }

  if (showBrochureModal) {
    return (
      <div className="lead-brochure-card" style={{ padding: '2.5rem 2rem', textAlign: 'center', width: '100%' }}>
        <CheckCircle2 size={56} color="var(--color-primary)" style={{ margin: '0 auto 1.25rem auto' }} />
        <h2 className="text-2xl font-bold mb-2 text-main">Lead Created!</h2>
        <p className="text-muted mb-6 text-sm" style={{ lineHeight: '1.5' }}>How would you like to send the brochure for the selected machine to the customer?</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <button className="btn" style={{ border: '1px solid var(--color-border)', padding: '0.5rem 1.25rem', borderRadius: '8px' }} onClick={() => onClose(true)}>Skip</button>
          <button className="btn btn-primary flex items-center gap-2" style={{ padding: '0.5rem 1.25rem', borderRadius: '8px' }} onClick={sendBrochureViaEmail}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            Send via Email
          </button>
          <button className="btn flex items-center gap-2" style={{ backgroundColor: '#25D366', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '8px' }} onClick={sendBrochureViaWhatsApp}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 3.4L3 21" /><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" /></svg>
            Send via WhatsApp
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
      <div
        className="modal-header"
        style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', flexShrink: 0 }}
      >
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>
          {initialData ? 'Edit Lead' : 'Add New Lead'}
        </h2>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }} title="Close">
          <X size={20} />
        </button>
      </div>

      <div className="form-modal-body" style={{ padding: '1.5rem', flex: 1, overflowY: 'auto' }}>
        <form onSubmit={handleSave} className="flex flex-col" noValidate>
          <fieldset disabled={isViewMode} style={{ border: 'none', padding: 0, margin: 0 }}>
            {/* Section 1: Agency/Shop Details */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)' }}>
                <Building2 size={20} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Agency / Shop Details</h3>
              </div>
              
              <div className="form-grid-3">

                                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Lead Source <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select Source..."
                    value={leadSource}
                    onChange={(val) => {
                      setLeadSource(val);
                      if (errors.leadSource) setErrors(prev => ({ ...prev, leadSource: null }));
                    }}
                    options={leadSources.map(s => ({ value: s.name, label: s.name }))}
                  />
                  {errors.leadSource && <div className="text-danger text-xs mt-1">{errors.leadSource}</div>}
                </div>

                {isReferenceSource && (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Reference Name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <input
                      type="text"
                      className="input-field"
                      required
                      placeholder="Enter reference person / firm name"
                      value={referenceName}
                      onChange={e => {
                        setReferenceName(e.target.value);
                        if (errors.referenceName) setErrors(prev => ({ ...prev, referenceName: null }));
                      }}
                    />
                    {errors.referenceName && <div className="text-danger text-xs mt-1">{errors.referenceName}</div>}
                  </div>
                )}

                {isSocialMediaSource && (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Social Media Platform <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <Dropdown
                      placeholder="Select Platform..."
                      value={socialMediaPlatform}
                      onChange={(val) => {
                        setSocialMediaPlatform(val);
                        if (errors.socialMediaPlatform) setErrors(prev => ({ ...prev, socialMediaPlatform: null }));
                      }}
                      options={SOCIAL_MEDIA_OPTIONS}
                    />
                    {errors.socialMediaPlatform && <div className="text-danger text-xs mt-1">{errors.socialMediaPlatform}</div>}
                  </div>
                )}

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Agency / Shop Type <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select Type..."
                    value={agencyType}
                    onChange={(val) => {
                      setAgencyType(val);
                      if (val === 'other') setIsOtherAgencyType(true);
                      else setIsOtherAgencyType(false);
                    }}
                    options={[
                      { value: 'copy shop', label: 'Copy Shop' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                  {errors.agencyType && <div className="text-danger text-xs mt-1">{errors.agencyType}</div>}
                </div>

                {isOtherAgencyType ? (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Other Type <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <input type="text" className="input-field" required placeholder="Specify other type" value={otherAgencyType} onChange={e => setOtherAgencyType(e.target.value)} />
                    {errors.otherAgencyType && <div className="text-danger text-xs mt-1">{errors.otherAgencyType}</div>}
                  </div>
                ) : (!isReferenceSource && !isSocialMediaSource ? <div></div> : null)}

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Agency / Shop Name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input type="text" className="input-field" required placeholder="e.g. Acme Printing Shop" value={shopName} onChange={e => setShopName(e.target.value)} />
                  {errors.shopName && <div className="text-danger text-xs mt-1">{errors.shopName}</div>}
                </div>

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Phone Number</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1px', top: '1px', bottom: '1px', display: 'flex', alignItems: 'center', padding: '0 0.75rem', backgroundColor: '#f1f5f9', borderRight: '1px solid var(--color-border)', borderTopLeftRadius: 'calc(var(--border-radius-sm) - 1px)', borderBottomLeftRadius: 'calc(var(--border-radius-sm) - 1px)', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.9rem', pointerEvents: 'none' }}>+91</span>
                    <input type="tel" className="input-field" required placeholder="98765 43210" value={phone} maxLength="10" onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} style={{ width: '100%', paddingLeft: '3.5rem' }} />
                  </div>
                  {errors.phone && <div className="text-danger text-xs mt-1">{errors.phone}</div>}
                </div>

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Email Address</label>
                  <input type="email" className="input-field" required placeholder="email@example.com" value={email} onChange={e => setEmail(e.target.value)} />
                  {errors.email && <div className="text-danger text-xs mt-1">{errors.email}</div>}
                </div>

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Address Line 1</label>
                  <input type="text" className="input-field" required placeholder="Street Address" value={address} onChange={e => setAddress(e.target.value)} />
                  {errors.address && <div className="text-danger text-xs mt-1">{errors.address}</div>}
                </div>

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Address Line 2</label>
                  <input type="text" className="input-field" placeholder="Apt, Suite, etc." value={address2} onChange={e => setAddress2(e.target.value)} />
                </div>

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>State <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select State..."
                    searchable={true}
                    value={state}
                    onChange={(val) => {
                      setState(val);
                      if (val === 'Other') {
                        setIsOtherState(true);
                        setIsOtherCity(true);
                        setCity('Other');
                      } else {
                        setIsOtherState(false);
                        const availableCities = getCitiesForState(val);
                        if (!availableCities.includes(city) && city !== 'Other') {
                          setCity(availableCities[0] || '');
                        }
                      }
                      if (errors.state) setErrors(prev => ({ ...prev, state: null }));
                    }}
                    options={getStateOptions(state)}
                  />
                  {errors.state && <div className="text-danger text-xs mt-1">{errors.state}</div>}
                </div>

                {isOtherState && (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Specify State <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Enter State Name"
                      value={otherState}
                      onChange={e => {
                        setOtherState(e.target.value);
                        if (errors.state) setErrors(prev => ({ ...prev, state: null }));
                      }}
                    />
                  </div>
                )}

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>City <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select City..."
                    searchable={true}
                    value={city}
                    onChange={(val) => {
                      setCity(val);
                      if (val === 'Other') {
                        setIsOtherCity(true);
                      } else {
                        setIsOtherCity(false);
                      }
                      if (errors.city) setErrors(prev => ({ ...prev, city: null }));
                    }}
                    options={getCityOptions(state, city)}
                  />
                  {errors.city && <div className="text-danger text-xs mt-1">{errors.city}</div>}
                </div>

                {isOtherCity && (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Specify City <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Enter City Name"
                      value={otherCity}
                      onChange={e => {
                        setOtherCity(e.target.value);
                        if (errors.city) setErrors(prev => ({ ...prev, city: null }));
                      }}
                    />
                  </div>
                )}

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Pincode</label>
                  <input type="text" className="input-field" required value={pincode} onChange={e => setPincode(e.target.value)} />
                  {errors.pincode && <div className="text-danger text-xs mt-1">{errors.pincode}</div>}
                </div>
              </div>
            </div>

            {/* Section 2: Contact Details */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={20} color="var(--color-primary)" />
                  <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Contact Details</h3>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none', fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)' }}>
                  <input
                    type="checkbox"
                    checked={sameAsShop}
                    onChange={(e) => handleSameAsShopChange(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                  />
                  <span>Same as shop/agency details</span>
                </label>
              </div>
              <div className="form-grid-3">
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Contact Person <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input
                    type="text"
                    className="input-field"
                    required
                    placeholder="Full Name"
                    value={contactPerson}
                    onChange={e => {
                      setContactPerson(e.target.value);
                      if (sameAsShop) setSameAsShop(false);
                      if (errors.contactPerson) setErrors(prev => ({ ...prev, contactPerson: null }));
                    }}
                  />
                  {errors.contactPerson && <div className="text-danger text-xs mt-1">{errors.contactPerson}</div>}
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Phone Number</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1px', top: '1px', bottom: '1px', display: 'flex', alignItems: 'center', padding: '0 0.75rem', backgroundColor: '#f1f5f9', borderRight: '1px solid var(--color-border)', borderTopLeftRadius: 'calc(var(--border-radius-sm) - 1px)', borderBottomLeftRadius: 'calc(var(--border-radius-sm) - 1px)', color: 'var(--color-text-muted)', fontWeight: '600', fontSize: '0.9rem', pointerEvents: 'none' }}>+91</span>
                    <input
                      type="tel"
                      className="input-field"
                      required
                      placeholder="98765 43210"
                      value={contactPhone}
                      maxLength="10"
                      onChange={e => {
                        setContactPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                        if (sameAsShop) setSameAsShop(false);
                        if (errors.contactPhone) setErrors(prev => ({ ...prev, contactPhone: null }));
                      }}
                      style={{ width: '100%', paddingLeft: '3.5rem' }}
                    />
                  </div>
                  {errors.contactPhone && <div className="text-danger text-xs mt-1">{errors.contactPhone}</div>}
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Email Address</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="email@example.com"
                    value={contactEmail}
                    onChange={e => {
                      setContactEmail(e.target.value);
                      if (sameAsShop) setSameAsShop(false);
                      if (errors.contactEmail) setErrors(prev => ({ ...prev, contactEmail: null }));
                    }}
                  />
                  {errors.contactEmail && <div className="text-danger text-xs mt-1">{errors.contactEmail}</div>}
                </div>
              </div>
            </div>

            {/* Section 3: Product Interest */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)' }}>
                <Tag size={20} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Product Interest</h3>
              </div>
              <div className="form-grid-3">
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Interest Type <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select Interest..."
                    value={interestedType}
                    onChange={handleInterestTypeChange}
                    options={getInterestTypeOptions()}
                  />
                  {errors.interestedType && <div className="text-danger text-xs mt-1">{errors.interestedType}</div>}
                </div>

                {isMachineInterest(interestedType) && (
                  <>
                    <div className="fade-in">
                      <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Machine Type <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                      <Dropdown
                        placeholder="Select Machine Type..."
                        value={machineType}
                        onChange={(val) => {
                          setMachineType(val);
                          if (errors.machineType) setErrors(prev => ({ ...prev, machineType: null }));
                        }}
                        options={[
                          { value: 'rental', label: 'Rental Machine' },
                          { value: 'new', label: 'New Machine' },
                          { value: 'rc', label: 'RC Machine' }
                        ]}
                      />
                      {errors.machineType && <div className="text-danger text-xs mt-1">{errors.machineType}</div>}
                    </div>
                    <div className="fade-in">
                      <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Machine Brand <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                      <Dropdown
                        placeholder="Select Machine..."
                        value={machineBrand}
                        onChange={(val) => {
                          setMachineBrand(val);
                          if (errors.machineBrand) setErrors(prev => ({ ...prev, machineBrand: null }));
                        }}
                        options={[
                          ...machines.map(m => ({ value: m.name, label: m.name })),
                          ...(machineBrand && !machines.some(m => m.name === machineBrand) ? [{ value: machineBrand, label: machineBrand }] : [])
                        ]}
                      />
                      {errors.machineBrand && <div className="text-danger text-xs mt-1">{errors.machineBrand}</div>}
                    </div>
                    <div className="fade-in">
                      <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Machine Model <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                      <input
                        type="text"
                        className="input-field"
                        value={machineModel}
                        onChange={(e) => {
                          setMachineModel(e.target.value);
                          if (errors.machineModel) setErrors(prev => ({ ...prev, machineModel: null }));
                        }}
                        placeholder="e.g. IR 3225"
                        required
                      />
                      {errors.machineModel && <div className="text-danger text-xs mt-1">{errors.machineModel}</div>}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Section 4: Tracking & Assignment */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)' }}>
                <CheckCircle2 size={20} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Tracking & Assignment</h3>
              </div>
              <div className="form-grid-3">
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Initial Status <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select Status..."
                    value={status}
                    onChange={(val) => {
                      if (val !== status) {
                        setStatus(val);
                        if (errors.status) setErrors(prev => ({ ...prev, status: null }));
                        const isNewFollowUp = val && (
                          val.toLowerCase().includes('schedule') ||
                          val.toLowerCase().includes('follow') ||
                          val.toLowerCase().includes('visit')
                        );
                        if (isNewFollowUp) {
                          if (followUpDays === '' || followUpDays === null || followUpDays === undefined) {
                            handleFollowUpDaysChange(0);
                          }
                          if (!followUpTime) {
                            const now = new Date();
                            const hours = String(now.getHours()).padStart(2, '0');
                            const mins = String(now.getMinutes()).padStart(2, '0');
                            setFollowUpTime(`${hours}:${mins}`);
                          }
                        } else {
                          setFollowUpDays('');
                          setFollowUpTime('');
                          setFollowUpDate(null);
                        }
                      }
                    }}
                    options={statuses.map(s => ({ value: s.name, label: s.name }))}
                  />
                  {errors.status && <div className="text-danger text-xs mt-1">{errors.status}</div>}
                </div>

                {canAssignExecutive && (
                  <div>
                    <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Assign To Executive <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                    <Dropdown
                      placeholder="Select Executive..."
                      value={assignee || ''}
                      onChange={(val) => {
                        setAssignee(val);
                      }}
                      options={[
                        ...(currentUser?.name && !users.some(u => u.name === currentUser.name) ? [{ value: currentUser.name, label: `${currentUser.name} (You)` }] : []),
                        ...users.map(u => ({ value: u.name, label: `${u.name} (${u.role})` }))
                      ]}
                    />
                    {errors.assignee && <div className="text-danger text-xs mt-1">{errors.assignee}</div>}
                  </div>
                )}

                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Customer Type <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <Dropdown
                    placeholder="Select Customer Type..."
                    value={customerType}
                    onChange={(val) => setCustomerType(val)}
                    options={customerTypes.map(c => ({ value: c.name, label: c.name }))}
                  />
                  {errors.customerType && <div className="text-danger text-xs mt-1">{errors.customerType}</div>}
                </div>

                {status && (status.toLowerCase().includes('schedule') || status.toLowerCase().includes('follow') || status.toLowerCase().includes('visit')) && (
                  <div className="fade-in form-grid-col-full" style={{ backgroundColor: '#f0f9ff', padding: '1rem 1.25rem', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                    <div className="form-grid-3">
                      <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#0369a1', fontSize: '0.875rem' }}>
                          {status?.toLowerCase().includes('visit') ? 'Visit In (Days)' : 'Follow-up In (Days)'} <span style={{ color: 'var(--color-danger)' }}>*</span>
                        </label>
                        <input
                          type="number"
                          className="input-field"
                          placeholder="e.g. 10"
                          min="0"
                          value={followUpDays}
                          onChange={(e) => handleFollowUpDaysChange(e.target.value)}
                          required
                          style={{ backgroundColor: 'white' }}
                        />
                        {errors.followUpDays && <div className="text-danger text-xs mt-1">{errors.followUpDays}</div>}
                      </div>
                      <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#0369a1', fontSize: '0.875rem' }}>
                          {status?.toLowerCase().includes('visit') ? 'Calculated Visit Date' : 'Calculated Follow-up Date'}
                        </label>
                        <div className="custom-datepicker-wrapper">
                          <DatePicker
                            selected={followUpDate ? new Date(followUpDate) : null}
                            onChange={(date) => handleFollowUpDateChange(date)}
                            className="input-field"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="DD/MM/YYYY"
                            minDate={new Date()}
                            style={{ backgroundColor: 'white' }}
                          />
                        </div>
                        {errors.followUpDate && <div className="text-danger text-xs mt-1">{errors.followUpDate}</div>}
                      </div>
                      <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#0369a1', fontSize: '0.875rem' }}>
                          {status?.toLowerCase().includes('visit') ? 'Visit Time' : 'Follow-up Time'} <span style={{ color: 'var(--color-danger)' }}>*</span>
                        </label>
                        <div className="custom-datepicker-wrapper">
                          <DatePicker
                            selected={parseTimeToDate(followUpTime, followUpDate)}
                            onChange={(date) => {
                              if (date) {
                                const hours = String(date.getHours()).padStart(2, '0');
                                const mins = String(date.getMinutes()).padStart(2, '0');
                                setFollowUpTime(`${hours}:${mins}`);
                                if (errors.followUpTime) setErrors(prev => ({ ...prev, followUpTime: null }));
                              } else {
                                setFollowUpTime('');
                              }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="hh:mm aa"
                            className="input-field"
                            placeholderText="Select Time"
                            required
                          />
                        </div>
                        {errors.followUpTime && <div className="text-danger text-xs mt-1">{errors.followUpTime}</div>}
                      </div>
                    </div>
                    {/* Executive Schedule Preview */}
                    {followUpDate && assignee && (
                      <div className="mt-3 p-3 bg-white rounded-lg border" style={{ borderColor: '#bae6fd' }}>
                        <h4 className="text-xs font-semibold mb-2" style={{ color: '#0369a1' }}>Executive's Schedule for {new Date(followUpDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</h4>
                        {isFetchingSchedule ? (
                          <div className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="animate-spin" size={14} /> Loading schedule...</div>
                        ) : (executiveSchedule.followups?.length === 0 && executiveSchedule.deliveries?.length === 0) ? (
                          <div className="text-xs text-gray-500">No scheduled events for this day. Free to schedule!</div>
                        ) : (
                          <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto pr-1">
                            {executiveSchedule.followups?.map((f, i) => (
                              <div key={`f-${i}`} className="text-xs p-2 bg-white border border-slate-200 rounded-lg flex flex-col items-start gap-1 w-max shadow-sm">
                                <span className="font-medium text-slate-800">{f.lead?.companyName || f.lead?.contactPerson || 'Lead'}</span>
                                <span className="text-slate-500 font-medium">{new Date(f.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            ))}
                            {executiveSchedule.deliveries?.map((d, i) => (
                              <div key={`d-${i}`} className="text-xs p-2 bg-white border border-slate-200 rounded-lg flex flex-col items-start gap-1 w-max shadow-sm">
                                <span className="font-medium text-slate-800">{d.title || d.partyName || 'Order'}</span>
                                <span className="text-slate-500 font-medium">{new Date(d.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Section 5: Additional Notes */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)' }}>
                <FileText size={20} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Additional Notes</h3>
              </div>
              <textarea className="input-field w-full" rows="3" placeholder="Add any initial conversation notes, special requirements, or follow-up instructions here..." value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight: '80px', fontSize: '0.875rem', padding: '0.75rem 1rem' }}></textarea>
            </div>

            {/* Section 6: Visiting Card Images */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 border-b pb-2.5" style={{ borderColor: 'var(--color-border)' }}>
                <FileText size={20} color="var(--color-primary)" />
                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>Visiting Card (Optional)</h3>
              </div>
              <div className="form-grid-2">
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Front Side Image</label>
                  <div style={{ border: '2px dashed var(--color-border)', borderRadius: '10px', padding: '1rem', textAlign: 'center', backgroundColor: frontCard ? 'var(--color-primary-light)' : '#f8fafc', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', height: '135px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <input type="file" accept="image/*" onChange={(e) => setFrontCard(e.target.files[0])} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }} />
                    {frontCard ? (
                      <img
                        src={typeof frontCard === 'string' ? frontCard : URL.createObjectURL(frontCard)}
                        alt="Front Card"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', zIndex: 1 }}
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <UploadCloud size={24} color={'var(--color-text-muted)'} />
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)' }}>
                          Click to upload front side
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Back Side Image</label>
                  <div style={{ border: '2px dashed var(--color-border)', borderRadius: '10px', padding: '1rem', textAlign: 'center', backgroundColor: backCard ? 'var(--color-primary-light)' : '#f8fafc', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', height: '135px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <input type="file" accept="image/*" onChange={(e) => setBackCard(e.target.files[0])} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }} />
                    {backCard ? (
                      <img
                        src={typeof backCard === 'string' ? backCard : URL.createObjectURL(backCard)}
                        alt="Back Card"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', zIndex: 1 }}
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <UploadCloud size={24} color={'var(--color-text-muted)'} />
                        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-main)' }}>
                          Click to upload back side
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </fieldset>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem', paddingTop: '1.25rem', borderTop: '1px solid var(--color-border)' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.5rem 1.5rem', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'white', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem' }}>Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '8px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500', fontSize: '0.875rem' }}>
              {isSubmitting && <Loader2 size={16} className="spinner-icon" />}
              {isSubmitting ? 'Saving...' : (initialData ? 'Save Changes' : 'Create Lead')}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AddLead;
